import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { collection, collectionGroup, getDocs, doc, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import { db } from '../firebase';

const MENU = [
  { id: 'overview',  label: 'ダッシュボード', icon: '📊' },
  { id: 'users',     label: 'ユーザー管理',   icon: '👥' },
  { id: 'revenue',   label: '売上・決済',     icon: '💰' },
  { id: 'logs',      label: '学習ログ分析',   icon: '📈' },
  { id: 'notice',    label: 'お知らせ配信',   icon: '🔔' },
  { id: 'settings',  label: 'サービス設定',   icon: '⚙️' },
  { id: 'teams',     label: 'チーム管理',     icon: '🏢' },
  { id: 'beta',      label: 'ベータ管理',     icon: '🧪' },
  { id: 'coaching',  label: 'コーチング顧客', icon: '🎯' },
];

const PLAN_LABELS = { free: 'Free', student: 'Student', standard: 'Standard', business: 'Business' };
const PLAN_COLORS = { free: '#9ca3af', student: '#3b82f6', standard: '#8b5cf6', business: '#f59e0b' };

// Firestore Timestamp・文字列・数値すべてに対応
const formatDate = (createdAt) => {
  if (!createdAt) return '-';
  try {
    // Firestore Timestamp オブジェクト
    if (createdAt?.toDate) return createdAt.toDate().toLocaleDateString('ja-JP');
    // 文字列 or 数値
    const d = new Date(createdAt);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('ja-JP');
  } catch {
    return '-';
  }
};

// 今月かどうか判定（Timestamp・文字列両対応）
const isThisMonth = (createdAt) => {
  if (!createdAt) return false;
  try {
    const d = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  } catch {
    return false;
  }
};

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('overview');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, free: 0, newThisMonth: 0 });

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(list);
      setStats({
        total: list.length,
        paid: list.filter(u => u.subscription?.planId && u.subscription.planId !== 'free').length,
        free: list.filter(u => !u.subscription?.planId || u.subscription.planId === 'free').length,
        newThisMonth: list.filter(u => isThisMonth(u.createdAt)).length,
      });
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const updateUserPlan = async (uid, planId) => {
    try {
      await updateDoc(doc(db, 'users', uid), { 'subscription.planId': planId });
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, subscription: { ...u.subscription, planId } } : u));
    } catch(e) { console.error(e); }
  };

  const updateUserRole = async (uid, role) => {
    try {
      await updateDoc(doc(db, 'users', uid), { role });
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, role } : u));
    } catch(e) { console.error(e); }
  };

  // ── 概要タブ ──
  const OverviewTab = () => (
    <div>
      <h2 style={styles.pageTitle}>ダッシュボード</h2>
      <div style={styles.statGrid}>
        {[
          { label: '総ユーザー数', value: stats.total,        icon: '👥', color: '#3b82f6' },
          { label: '有料ユーザー', value: stats.paid,         icon: '💎', color: '#8b5cf6' },
          { label: '無料ユーザー', value: stats.free,         icon: '🆓', color: '#9ca3af' },
          { label: '今月の新規',   value: stats.newThisMonth, icon: '🌱', color: '#10b981' },
        ].map(s => (
          <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📊 プラン別内訳</h3>
        {Object.entries(PLAN_LABELS).map(([planId, label]) => {
          const count = users.filter(u => (u.subscription?.planId || 'free') === planId).length;
          const pct = stats.total > 0 ? Math.round(count / stats.total * 100) : 0;
          return (
            <div key={planId} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: PLAN_COLORS[planId], fontWeight: 700 }}>{label}</span>
                <span style={{ color: '#374151' }}>{count}人 ({pct}%)</span>
              </div>
              <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999 }}>
                <div style={{ height: 8, width: `${pct}%`, background: PLAN_COLORS[planId], borderRadius: 999, transition: 'width 0.5s' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── ユーザー管理タブ ──
  const UsersTab = () => {
    const [search, setSearch] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const filtered = users.filter(u => {
      const matchSearch = !search || u.email?.includes(search) || u.displayName?.includes(search);
      const matchPlan = filterPlan === 'all' || (u.subscription?.planId || 'free') === filterPlan;
      return matchSearch && matchPlan;
    });
    return (
      <div>
        <h2 style={styles.pageTitle}>ユーザー管理</h2>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            placeholder="メール・名前で検索..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.input}
          />
          <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} style={styles.select}>
            <option value="all">全プラン</option>
            {Object.entries(PLAN_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={fetchUsers} style={{ ...styles.btnSecondary }}>🔄 更新</button>
        </div>
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['ユーザー', 'プラン', '権限', '登録日', '操作'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{u.displayName || '未設定'}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div>
                    </td>
                    <td style={styles.td}>
                      <select
                        value={u.subscription?.planId || 'free'}
                        onChange={e => updateUserPlan(u.id, e.target.value)}
                        style={{ ...styles.select, fontSize: 11, padding: '4px 8px',
                          color: PLAN_COLORS[u.subscription?.planId || 'free'] }}
                      >
                        {Object.entries(PLAN_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <select
                        value={u.role || 'user'}
                        onChange={e => updateUserRole(u.id, e.target.value)}
                        style={{ ...styles.select, fontSize: 11, padding: '4px 8px' }}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        <option value="master">master</option>
                      </select>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 11, color: '#6b7280' }}>
                        {formatDate(u.createdAt)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{ fontSize: 11, padding: '3px 8px', background: '#f3f4f6',
                          borderRadius: 6, color: '#374151', cursor: 'pointer' }}
                        onClick={() => navigator.clipboard?.writeText(u.id)}
                      >
                        IDコピー
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
                該当するユーザーが見つかりません
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
            {filtered.length} / {users.length} 件表示
          </div>
        </div>
      </div>
    );
  };

  // ── お知らせ配信タブ ──
  const NoticeTab = () => {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const handleSend = async () => {
      if (!title || !body) return;
      setSending(true);
      try {
        await addDoc(collection(db, 'notices'), {
          title, body,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          active: true,
        });
        setSent(true);
        setTitle(''); setBody('');
        setTimeout(() => setSent(false), 3000);
      } catch(e) { console.error(e); }
      setSending(false);
    };
    return (
      <div>
        <h2 style={styles.pageTitle}>お知らせ配信</h2>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>新しいお知らせを作成</h3>
          <div style={{ marginBottom: 16 }}>
            <label style={styles.label}>タイトル</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="お知らせのタイトル" style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={styles.label}>本文</label>
            <textarea value={body} onChange={e => setBody(e.target.value)}
              placeholder="お知らせの内容を入力してください..."
              rows={5} style={{ ...styles.input, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <button onClick={handleSend} disabled={sending || !title || !body} style={{
            ...styles.btnPrimary,
            opacity: (sending || !title || !body) ? 0.5 : 1,
            cursor: (sending || !title || !body) ? 'not-allowed' : 'pointer',
          }}>
            {sending ? '送信中...' : '📢 全ユーザーに配信する'}
          </button>
          {sent && <div style={{ color: '#059669', fontSize: 13, marginTop: 12 }}>✅ 配信しました！</div>}
        </div>
      </div>
    );
  };

  // ── コーチング顧客タブ ──
  const CoachingTab = () => {
    const paidUsers = users.filter(u => u.subscription?.planId && u.subscription.planId !== 'free');
    return (
      <div>
        <h2 style={styles.pageTitle}>コーチング顧客管理</h2>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>有料プラン顧客一覧（{paidUsers.length}名）</h3>
          {paidUsers.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e0e7ff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#4338ca', flexShrink: 0 }}>
                {(u.displayName || u.email || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{u.displayName || '未設定'}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.email}</div>
                <div style={{ fontSize: 10, color: '#b0b8c1', marginTop: 2 }}>登録: {formatDate(u.createdAt)}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px',
                borderRadius: 20,
                background: (PLAN_COLORS[u.subscription?.planId] || '#9ca3af') + '20',
                color: PLAN_COLORS[u.subscription?.planId] || '#9ca3af' }}>
                {PLAN_LABELS[u.subscription?.planId] || 'Free'}
              </span>
              <a href={`mailto:${u.email}`} style={{ fontSize: 11, padding: '5px 12px',
                background: '#f3f4f6', borderRadius: 8, color: '#374151',
                textDecoration: 'none', border: '1px solid #e5e7eb' }}>
                メール
              </a>
            </div>
          ))}
          {paidUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
              有料ユーザーがまだいません
            </div>
          )}
        </div>
      </div>
    );
  };


  // ── チーム管理タブ ──
  const TeamsTab = () => {
    const [teams, setTeams]           = useState([]);
    const [loadingT, setLoadingT]     = useState(true);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [groups, setGroups]         = useState([]);
    const [loadingG, setLoadingG]     = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(null); // { groupId, groupName }
    const [addEmail, setAddEmail]       = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [allUsers, setAllUsers]       = useState([]);
    const [addRole, setAddRole]       = useState('member');
    const [addingMember, setAddingMember] = useState(false);
    const [addError, setAddError]     = useState('');

    const toDate = (v) => {
      if (!v) return null;
      try {
        if (v?.toDate) return v.toDate();
        if (v?.seconds) return new Date(v.seconds * 1000);
        const d = new Date(v); return isNaN(d.getTime()) ? null : d;
      } catch { return null; }
    };
    const fmt = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('ja-JP') : '-'; };

    const fetchTeams = async () => {
      setLoadingT(true);
      try {
        const snap = await getDocs(collection(db, 'teams'));
        const uSnap = await getDocs(collection(db, 'users'));
        const usersMap = Object.fromEntries(uSnap.docs.map(d => [d.id, d.data()]));
        // 全ユーザーリストを保存（検索用）
        setAllUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const list = await Promise.all(snap.docs.map(async (tDoc) => {
          const tData = { id: tDoc.id, ...tDoc.data() };
          const owner = usersMap[tData.ownerId];
          tData.ownerName  = owner?.displayName || owner?.email || '不明';
          tData.ownerEmail = owner?.email || '';
          try {
            const gSnap = await getDocs(collection(db, `teams/${tDoc.id}/groups`));
            tData.groupCount = gSnap.size;
            let memberCount = 0;
            for (const gDoc of gSnap.docs) {
              const mSnap = await getDocs(collection(db, `teams/${tDoc.id}/groups/${gDoc.id}/members`));
              memberCount += mSnap.size;
            }
            tData.memberCount = memberCount;
          } catch { tData.groupCount = 0; tData.memberCount = 0; }
          return tData;
        }));
        setTeams(list);
      } catch(e) { console.error(e); }
      setLoadingT(false);
    };

    useEffect(() => { fetchTeams(); }, []);

    const fetchGroups = async (teamId) => {
      setLoadingG(true);
      try {
        const gSnap = await getDocs(collection(db, `teams/${teamId}/groups`));
        const gList = await Promise.all(gSnap.docs.map(async (gDoc) => {
          const gData = { id: gDoc.id, ...gDoc.data() };
          const mSnap = await getDocs(collection(db, `teams/${teamId}/groups/${gDoc.id}/members`));
          gData.members = mSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          return gData;
        }));
        setGroups(gList);
      } catch(e) { console.error(e); }
      setLoadingG(false);
    };

    const handleSelectTeam = async (team) => {
      setSelectedTeam(team);
      await fetchGroups(team.id);
    };

    const handleDeleteMember = async (groupId, memberId, name) => {
      if (!window.confirm(`「${name}」を削除しますか？`)) return;
      setDeletingId(memberId);
      try {
        await deleteDoc(doc(db, `teams/${selectedTeam.id}/groups/${groupId}/members`, memberId));
        setGroups(prev => prev.map(g => g.id === groupId
          ? { ...g, members: g.members.filter(m => m.id !== memberId) }
          : g
        ));
      } catch(e) { alert(e.message); }
      setDeletingId(null);
    };

    const handleRoleChange = async (groupId, memberId, newRole) => {
      setUpdatingId(memberId);
      try {
        await updateDoc(doc(db, `teams/${selectedTeam.id}/groups/${groupId}/members`, memberId), { role: newRole });
        setGroups(prev => prev.map(g => g.id === groupId
          ? { ...g, members: g.members.map(m => m.id === memberId ? { ...m, role: newRole } : m) }
          : g
        ));
      } catch(e) { alert(e.message); }
      setUpdatingId(null);
    };

    const handleAddMember = async () => {
      if (!selectedUser) return;
      setAddingMember(true); setAddError('');
      try {
        const userData = selectedUser;
        const userDocId = selectedUser.id;

        // リーダー重複チェック
        if (addRole === 'leader') {
          const group = groups.find(g => g.id === showAddModal.groupId);
          const hasLeader = group?.members.some(m => m.role === 'leader');
          if (hasLeader) throw new Error('このグループにはすでにリーダーがいます');
        }
        // メンバー上限チェック
        if (addRole === 'member') {
          const group = groups.find(g => g.id === showAddModal.groupId);
          const memberCount = group?.members.filter(m => m.role === 'member').length || 0;
          if (memberCount >= 3) throw new Error('1グループのメンバーは3名まで（フォーマンセル：リーダー1名＋メンバー3名）');
        }

        // リーダー重複チェック
        if (addRole === 'leader') {
          const group = groups.find(g => g.id === showAddModal.groupId);
          const hasLeader = group?.members.some(m => m.role === 'leader');
          if (hasLeader) throw new Error('このグループにはすでにリーダーがいます');
        }
        // メンバー上限チェック
        if (addRole === 'member') {
          const group = groups.find(g => g.id === showAddModal.groupId);
          const memberCount = group?.members.filter(m => m.role === 'member').length || 0;
          if (memberCount >= 3) throw new Error('メンバーは最大3名まで（フォーマンセル）');
        }

        await addDoc(collection(db, `teams/${selectedTeam.id}/groups/${showAddModal.groupId}/members`), {
          userId: userDocId,
          role: addRole,
          displayName: userData.displayName || userData.nickname || '',
          email: userData.email || '',
          joinedAt: serverTimestamp(),
          addedBy: 'admin',
        });

        await fetchGroups(selectedTeam.id);
        setShowAddModal(null); setAddEmail(''); setAddRole('member');
        setSearchQuery(''); setSelectedUser(null);
      } catch(e) { setAddError(e.message); }
      setAddingMember(false);
    };

    const ROLE_COLOR = { owner: '#f59e0b', leader: '#8b5cf6', member: '#3b82f6' };
    const ROLE_LABEL = { owner: 'オーナー', leader: 'リーダー', member: 'メンバー' };

    // チーム一覧
    if (!selectedTeam) return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ ...styles.pageTitle, marginBottom: 0 }}>チーム管理</h2>
          <button onClick={fetchTeams} style={styles.btnSecondary}>🔄 更新</button>
        </div>
        {loadingT ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>⏳ 読み込み中...</div>
        ) : teams.length === 0 ? (
          <div style={{ ...styles.card, textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
            <div style={{ fontSize: 14, color: '#9ca3af' }}>チームはまだありません</div>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>全チーム一覧（{teams.length}チーム）</h3>
            <table style={styles.table}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['チーム名','オーナー','グループ数','メンバー数','作成日','操作'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map(t => (
                  <tr key={t.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>{t.id.slice(0,8)}...</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.ownerName}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.ownerEmail}</div>
                    </td>
                    <td style={styles.td}><span style={{ fontSize: 14, fontWeight: 800, color: '#8b5cf6' }}>{t.groupCount}</span></td>
                    <td style={styles.td}><span style={{ fontSize: 14, fontWeight: 800, color: '#3b82f6' }}>{t.memberCount}</span></td>
                    <td style={styles.td}><span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(t.createdAt)}</span></td>
                    <td style={styles.td}>
                      <button onClick={() => handleSelectTeam(t)}
                        style={{ fontSize: 12, padding: '5px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, color: '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                        詳細
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );

    // チーム詳細
    return (
      <div>
        {/* メンバー追加モーダル */}
        {showAddModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={() => { setShowAddModal(null); setAddEmail(''); setAddError(''); setSearchQuery(''); setSelectedUser(null); }} />
            <div style={{ position: 'relative', background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 24px 48px rgba(0,0,0,0.25)' }}>

              {/* ヘッダー */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>メンバーを追加</h3>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>グループ：{showAddModal.groupName}</p>
              </div>

              {addError && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13, color: '#dc2626' }}>
                  ⚠️ {addError}
                </div>
              )}

              {/* ユーザー検索 */}
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>ユーザーを検索</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#9ca3af' }}>🔍</span>
                  <input
                    autoFocus
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setSelectedUser(null); setAddError(''); }}
                    placeholder="名前・メールアドレスで検索..."
                    style={{ ...styles.input, width: '100%', boxSizing: 'border-box', paddingLeft: 32 }}
                  />
                </div>
              </div>

              {/* 検索結果リスト */}
              {searchQuery.trim() && (
                <div style={{ marginBottom: 14, maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' }}>
                  {(() => {
                    const q = searchQuery.toLowerCase();
                    const existingIds = new Set(
                      groups.find(g => g.id === showAddModal.groupId)?.members?.map(m => m.userId) || []
                    );
                    const filtered = allUsers.filter(u =>
                      !existingIds.has(u.id) &&
                      ((u.email || '').toLowerCase().includes(q) ||
                       (u.displayName || '').toLowerCase().includes(q) ||
                       (u.nickname || '').toLowerCase().includes(q))
                    );
                    if (filtered.length === 0) return (
                      <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
                        該当するユーザーが見つかりません
                      </div>
                    );
                    return filtered.map(u => {
                      const isSel = selectedUser?.id === u.id;
                      return (
                        <div key={u.id} onClick={() => { setSelectedUser(u); setAddEmail(u.email || ''); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                            background: isSel ? '#eff6ff' : 'transparent',
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background 0.1s' }}
                          onMouseEnter={e => !isSel && (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={e => !isSel && (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: isSel ? '#4f46e5' : '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: isSel ? '#fff' : '#4338ca', flexShrink: 0 }}>
                            {(u.nickname || u.displayName || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.nickname || u.displayName || '未設定'}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                          </div>
                          {isSel && <span style={{ fontSize: 16, color: '#4f46e5' }}>✓</span>}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {/* 選択中ユーザー表示 */}
              {selectedUser && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                    {(selectedUser.nickname || selectedUser.displayName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{selectedUser.nickname || selectedUser.displayName}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{selectedUser.email}</div>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setAddEmail(''); setSearchQuery(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>✕</button>
                </div>
              )}

              {/* ロール選択 */}
              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>ロール</label>
                <select value={addRole} onChange={e => setAddRole(e.target.value)} style={{ ...styles.select, width: '100%' }}>
                  <option value="member">メンバー</option>
                  <option value="leader">リーダー</option>
                </select>
              </div>

              {/* ボタン */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowAddModal(null); setAddEmail(''); setAddError(''); setSearchQuery(''); setSelectedUser(null); }}
                  style={{ flex: 1, padding: '10px 0', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#374151', background: '#fff', cursor: 'pointer' }}>
                  キャンセル
                </button>
                <button onClick={handleAddMember} disabled={addingMember || !selectedUser}
                  style={{ flex: 1, padding: '10px 0', background: addingMember || !selectedUser ? '#c7d2fe' : 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, color: '#fff', cursor: addingMember || !selectedUser ? 'not-allowed' : 'pointer' }}>
                  {addingMember ? '追加中...' : '追加する'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => { setSelectedTeam(null); setGroups([]); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', fontSize: 13, fontWeight: 700, padding: 0 }}>
            ← チーム一覧に戻る
          </button>
          <span style={{ color: '#e5e7eb' }}>|</span>
          <h2 style={{ ...styles.pageTitle, margin: 0 }}>{selectedTeam.name}</h2>
          <button onClick={() => fetchGroups(selectedTeam.id)} style={{ ...styles.btnSecondary, marginLeft: 'auto' }}>🔄 更新</button>
        </div>

        {/* チーム情報 */}
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {[
              { label: 'オーナー', value: selectedTeam.ownerName, icon: '👑' },
              { label: 'グループ数', value: `${groups.length}グループ`, icon: '📁' },
              { label: '総メンバー数', value: `${groups.reduce((s,g) => s + g.members.length, 0)}人`, icon: '👥' },
              { label: '作成日', value: fmt(selectedTeam.createdAt), icon: '📅' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: 10 }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* グループ一覧 */}
        {loadingG ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>⏳ 読み込み中...</div>
        ) : groups.map(g => (
          <div key={g.id} style={{ ...styles.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: '#111827', margin: 0 }}>📁 {g.name}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{g.members.length}人</span>
                <button onClick={() => { setShowAddModal({ groupId: g.id, groupName: g.name }); setAddEmail(''); setAddRole('member'); setAddError(''); }}
                  style={{ fontSize: 12, padding: '5px 12px', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                  ＋ 追加
                </button>
              </div>
            </div>

            {g.members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>メンバーがいません</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['名前','メール','ロール','参加日','操作'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.members.map(m => (
                    <tr key={m.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#4338ca' }}>
                            {(m.displayName || m.email || '?')[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{m.displayName || '未設定'}</span>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#6b7280' }}>{m.email || '-'}</span></td>
                      <td style={styles.td}>
                        <select
                          value={m.role || 'member'}
                          onChange={e => handleRoleChange(g.id, m.id, e.target.value)}
                          disabled={updatingId === m.id}
                          style={{ fontSize: 11, border: `1.5px solid ${ROLE_COLOR[m.role] || '#e5e7eb'}`, borderRadius: 8, padding: '4px 8px',
                            color: ROLE_COLOR[m.role] || '#374151', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                          <option value="member">メンバー</option>
                          <option value="leader">リーダー</option>
                        </select>
                        {updatingId === m.id && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4 }}>更新中...</span>}
                      </td>
                      <td style={styles.td}><span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(m.joinedAt)}</span></td>
                      <td style={styles.td}>
                        <button onClick={() => handleDeleteMember(g.id, m.id, m.displayName || m.email)}
                          disabled={deletingId === m.id}
                          style={{ fontSize: 11, padding: '4px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>
                          {deletingId === m.id ? '...' : '削除'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    );
  };


  // ── 領収書発行 ──
  const generateReceipt = (payment, userName, userEmail) => {
    const doc2 = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // 領収書番号
    const receiptNo = `RCP-${payment.id.slice(0,8).toUpperCase()}-${new Date(payment.completedAt?.toDate?.() || payment.completedAt).getFullYear()}`;
    const issueDate = payment.completedAt?.toDate
      ? payment.completedAt.toDate().toLocaleDateString('ja-JP')
      : new Date(payment.completedAt).toLocaleDateString('ja-JP');

    const taxRate  = 0.10;
    const total    = Number(payment.amount) || 0;
    const taxBase  = Math.round(total / (1 + taxRate));
    const tax      = total - taxBase;

    const PLAN_LABELS = { free: 'Freeプラン', student: 'Studentプラン', standard: 'Standardプラン', business: 'Businessプラン' };

    // フォント設定（日本語対応のためUnicode文字をロード）
    doc2.setFont('helvetica');

    // ── ヘッダー背景 ──
    doc2.setFillColor(79, 70, 229); // indigo
    doc2.rect(0, 0, 210, 40, 'F');

    // ── タイトル ──
    doc2.setTextColor(255, 255, 255);
    doc2.setFontSize(24);
    doc2.text('RYOSHU-SHO / Receipt', 20, 18);
    doc2.setFontSize(10);
    doc2.text('NOBISHIRO - Tominaga Coaching Country Club, Inc.', 20, 28);

    // ── 領収書番号・発行日 ──
    doc2.setTextColor(200, 200, 255);
    doc2.setFontSize(9);
    doc2.text(`Receipt No: ${receiptNo}`, 130, 18);
    doc2.text(`Issue Date: ${issueDate}`, 130, 26);

    // ── 宛先 ──
    doc2.setTextColor(30, 30, 30);
    doc2.setFontSize(11);
    doc2.text('To:', 20, 55);
    doc2.setFontSize(14);
    doc2.setFont('helvetica', 'bold');
    doc2.text(userName || userEmail || 'Customer', 20, 63);
    doc2.setFont('helvetica', 'normal');
    doc2.setFontSize(10);
    doc2.setTextColor(100, 100, 100);
    doc2.text(userEmail || '', 20, 70);

    // ── 区切り線 ──
    doc2.setDrawColor(200, 200, 200);
    doc2.setLineWidth(0.3);
    doc2.line(20, 78, 190, 78);

    // ── 明細テーブルヘッダー ──
    doc2.setFillColor(245, 245, 250);
    doc2.rect(20, 82, 170, 10, 'F');
    doc2.setTextColor(80, 80, 80);
    doc2.setFontSize(9);
    doc2.setFont('helvetica', 'bold');
    doc2.text('Description', 25, 89);
    doc2.text('Amount', 165, 89, { align: 'right' });

    // ── 明細行 ──
    doc2.setFont('helvetica', 'normal');
    doc2.setFontSize(11);
    doc2.setTextColor(30, 30, 30);
    const planLabel = PLAN_LABELS[payment.planId] || payment.planId || 'Subscription';
    doc2.text(`NOBISHIRO ${planLabel} (Monthly)`, 25, 102);
    doc2.text(`JPY ${taxBase.toLocaleString()}`, 165, 102, { align: 'right' });

    // 消費税行
    doc2.setFontSize(9);
    doc2.setTextColor(120, 120, 120);
    doc2.text('Consumption Tax (10%)', 25, 112);
    doc2.text(`JPY ${tax.toLocaleString()}`, 165, 112, { align: 'right' });

    // ── 合計金額 ──
    doc2.setFillColor(79, 70, 229);
    doc2.rect(20, 120, 170, 16, 'F');
    doc2.setTextColor(255, 255, 255);
    doc2.setFontSize(13);
    doc2.setFont('helvetica', 'bold');
    doc2.text('Total (Tax Included)', 25, 131);
    doc2.text(`JPY ${total.toLocaleString()}`, 165, 131, { align: 'right' });

    // ── 支払情報 ──
    doc2.setFont('helvetica', 'normal');
    doc2.setFontSize(9);
    doc2.setTextColor(100, 100, 100);
    doc2.text('Payment Method: Credit Card (via UnivaPay)', 25, 148);
    doc2.text(`Transaction ID: ${payment.id.slice(0,16).toUpperCase()}`, 25, 155);
    doc2.text('Status: Paid', 25, 162);

    // ── 区切り線 ──
    doc2.setDrawColor(220, 220, 220);
    doc2.line(20, 170, 190, 170);

    // ── 発行者情報 ──
    doc2.setFontSize(10);
    doc2.setTextColor(30, 30, 30);
    doc2.setFont('helvetica', 'bold');
    doc2.text('Issuer:', 20, 182);
    doc2.setFont('helvetica', 'normal');
    doc2.text('Tominaga Coaching Country Club, Inc.', 20, 190);
    doc2.text('(Tominaga Coaching Country Club Kabushiki Gaisha)', 20, 197);
    doc2.setTextColor(100, 100, 100);
    doc2.setFontSize(9);
    doc2.text('Website: https://www.nobishiro-competency.com', 20, 205);
    doc2.text('Service: NOBISHIRO Skill Development Platform', 20, 212);

    // ── フッター ──
    doc2.setFillColor(245, 245, 250);
    doc2.rect(0, 270, 210, 27, 'F');
    doc2.setTextColor(150, 150, 150);
    doc2.setFontSize(8);
    doc2.text('This is an official receipt issued by Tominaga Coaching Country Club, Inc.', 105, 280, { align: 'center' });
    doc2.text('For inquiries, please contact us via the website above.', 105, 287, { align: 'center' });
    doc2.text(`Generated: ${new Date().toLocaleDateString('ja-JP')}  |  Receipt No: ${receiptNo}`, 105, 294, { align: 'center' });

    // DL
    doc2.save(`receipt_${receiptNo}.pdf`);
  };

  // ── 売上・決済管理タブ ──
  const RevenueTab = () => {
    const [payHistory, setPayHistory] = useState([]);
    const [loadingP, setLoadingP]     = useState(true);

    const PLAN_PRICES = { free: 0, student: 480, standard: 980, business: 4980 };
    const PLAN_COLORS = { free: '#9ca3af', student: '#3b82f6', standard: '#8b5cf6', business: '#f59e0b' };
    const PLAN_LABELS = { free: 'Free', student: 'Student', standard: 'Standard', business: 'Business' };

    const toDate = (v) => {
      if (!v) return null;
      try {
        if (v?.toDate) return v.toDate();
        if (v?.seconds) return new Date(v.seconds * 1000);
        const d = new Date(v); return isNaN(d.getTime()) ? null : d;
      } catch { return null; }
    };
    const fmt = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('ja-JP') : '-'; };

    useEffect(() => {
      (async () => {
        setLoadingP(true);
        try {
          const snap = await getDocs(collection(db, 'payment_history'));
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (toDate(b.completedAt)||0) - (toDate(a.completedAt)||0));
          setPayHistory(list);
        } catch(e) { console.error(e); }
        setLoadingP(false);
      })();
    }, []);

    // MRR計算（現在のプランから）
    const mrrByPlan = Object.keys(PLAN_PRICES).map(planId => {
      const count   = users.filter(u => (u.subscription?.planId || 'free') === planId).length;
      const revenue = count * PLAN_PRICES[planId];
      return { planId, label: PLAN_LABELS[planId], count, revenue, price: PLAN_PRICES[planId] };
    });
    const totalMRR  = mrrByPlan.reduce((s, p) => s + p.revenue, 0);
    const totalARR  = totalMRR * 12;
    const paidCount = users.filter(u => u.subscription?.planId && u.subscription.planId !== 'free').length;
    const arpu      = paidCount > 0 ? Math.round(totalMRR / paidCount) : 0;

    // 月別新規ユーザー（過去6ヶ月）
    const now = new Date();
    const monthlyData = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5-i), 1);
      const count = users.filter(u => {
        const cd = toDate(u.createdAt);
        return cd && cd.getFullYear() === d.getFullYear() && cd.getMonth() === d.getMonth();
      }).length;
      return { label: `${d.getMonth()+1}月`, count };
    });
    const maxCount = Math.max(...monthlyData.map(m => m.count), 1);

    // 月別売上（payment_historyから）
    const monthlyRevenue = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5-i), 1);
      const total = payHistory.filter(p => {
        const pd = toDate(p.completedAt);
        return pd && pd.getFullYear() === d.getFullYear() && pd.getMonth() === d.getMonth();
      }).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      return { label: `${d.getMonth()+1}月`, total };
    });
    const maxRevenue = Math.max(...monthlyRevenue.map(m => m.total), 1);

    const formatCurrency = (n) => '¥' + n.toLocaleString('ja-JP');

    return (
      <div>
        <h2 style={styles.pageTitle}>売上・決済管理</h2>

        {/* KPIカード */}
        <div style={styles.statGrid}>
          {[
            { label: 'MRR（月次売上）', value: formatCurrency(totalMRR), icon: '💰', color: '#10b981', sub: '月額合計' },
            { label: 'ARR（年次換算）', value: formatCurrency(totalARR), icon: '📈', color: '#3b82f6', sub: 'MRR × 12' },
            { label: '有料ユーザー数',  value: `${paidCount}人`, icon: '💎', color: '#8b5cf6', sub: `全体の${stats.total > 0 ? Math.round(paidCount/stats.total*100) : 0}%` },
            { label: 'ARPU', value: formatCurrency(arpu), icon: '👤', color: '#f59e0b', sub: '有料平均単価' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* プラン別内訳 */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>💳 プラン別売上内訳</h3>
          <table style={styles.table}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['プラン','単価','ユーザー数','月次売上','構成比'].map(h => <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {mrrByPlan.map(p => {
                const pct = totalMRR > 0 ? Math.round(p.revenue / totalMRR * 100) : 0;
                return (
                  <tr key={p.planId} style={styles.tr}>
                    <td style={styles.td}><span style={{ fontWeight: 700, color: PLAN_COLORS[p.planId] }}>{p.label}</span></td>
                    <td style={styles.td}>{formatCurrency(p.price)}/月</td>
                    <td style={styles.td}>{p.count}人</td>
                    <td style={styles.td}><span style={{ fontWeight: 700, color: p.revenue > 0 ? '#059669' : '#9ca3af' }}>{formatCurrency(p.revenue)}</span></td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 999, minWidth: 60 }}>
                          <div style={{ height: 6, width: `${pct}%`, background: PLAN_COLORS[p.planId], borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 11, color: '#6b7280', minWidth: 32 }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: '#f9fafb' }}>
                <td style={styles.td} colSpan={3}><span style={{ fontWeight: 700 }}>合計</span></td>
                <td style={styles.td}><span style={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>{formatCurrency(totalMRR)}</span></td>
                <td style={styles.td}><span style={{ fontSize: 12, color: '#6b7280' }}>100%</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* グラフ2段 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {/* 月別新規ユーザー */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📅 月別新規ユーザー（過去6ヶ月）</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
              {monthlyData.map((m, i) => {
                const h = Math.round((m.count / maxCount) * 100);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{m.count}</span>
                    <div style={{ width: '100%', height: `${Math.max(h, 4)}%`, minHeight: 4,
                      background: i === monthlyData.length-1 ? 'linear-gradient(180deg,#10b981,#059669)' : 'linear-gradient(180deg,#93c5fd,#3b82f6)',
                      borderRadius: '6px 6px 0 0' }} />
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{m.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 月別実績売上 */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>💴 月別決済金額（過去6ヶ月）</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
              {monthlyRevenue.map((m, i) => {
                const h = Math.round((m.total / maxRevenue) * 100);
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#374151' }}>¥{m.total.toLocaleString()}</span>
                    <div style={{ width: '100%', height: `${Math.max(h, 4)}%`, minHeight: 4,
                      background: i === monthlyRevenue.length-1 ? 'linear-gradient(180deg,#f59e0b,#d97706)' : 'linear-gradient(180deg,#fcd34d,#f59e0b)',
                      borderRadius: '6px 6px 0 0' }} />
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{m.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 決済履歴 */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📋 決済履歴</h3>
          {loadingP ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>⏳ 読み込み中...</div>
          ) : payHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
              決済履歴がありません<br />
              <span style={{ fontSize: 11 }}>UnivaPayのWebhookが正常に設定されると自動記録されます</span>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['日付','ユーザーID','プラン','金額','ステータス','領収書'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payHistory.slice(0, 20).map(p => (
                    <tr key={p.id} style={styles.tr}>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#6b7280' }}>{fmt(p.completedAt)}</span></td>
                      <td style={styles.td}><span style={{ fontSize: 11, color: '#374151', fontFamily: 'monospace' }}>{p.userId?.slice(0,12)}...</span></td>
                      <td style={styles.td}>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 700,
                          background: (PLAN_COLORS[p.planId] || '#9ca3af') + '18',
                          color: PLAN_COLORS[p.planId] || '#9ca3af' }}>
                          {PLAN_LABELS[p.planId] || p.planId}
                        </span>
                      </td>
                      <td style={styles.td}><span style={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(Number(p.amount) || 0)}</span></td>
                      <td style={styles.td}>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20,
                          background: p.status === 'completed' ? '#dcfce7' : '#fef3c7',
                          color: p.status === 'completed' ? '#166534' : '#92400e', fontWeight: 700 }}>
                          {p.status === 'completed' ? '✅ 完了' : '⏳ ' + p.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {p.status === 'completed' && (
                          <button
                            onClick={() => {
                              const u = users.find(u => u.id === p.userId);
                              generateReceipt(p, u?.displayName || u?.email || '顧客', u?.email || '');
                            }}
                            style={{ fontSize: 11, padding: '4px 10px', background: '#eff6ff',
                              border: '1px solid #bfdbfe', borderRadius: 8, color: '#3b82f6',
                              cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            📄 PDF
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payHistory.length > 20 && (
                <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 12 }}>
                  ※ 最新20件を表示中（全{payHistory.length}件）
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          ※ MRR/ARRはFirestoreのユーザープランから算出。実際の決済金額はUnivaPayのWebhook連携後に反映されます。
        </div>
      </div>
    );
  };

  // ── 学習ログ分析タブ ──
  const LogsTab = () => {
    const [allLogs, setAllLogs]     = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [fetched, setFetched]     = useState(false);

    const CATEGORIES = [
      { id: 'reading',  label: '読書・インプット',    emoji: '📚', color: '#3b82f6' },
      { id: 'practice', label: '実践・アウトプット',  emoji: '⚡', color: '#f59e0b' },
      { id: 'research', label: '調査・リサーチ',      emoji: '🔍', color: '#8b5cf6' },
      { id: 'meeting',  label: '議論・ミーティング',  emoji: '💬', color: '#10b981' },
      { id: 'creation', label: '制作・クリエイティブ', emoji: '🎨', color: '#ef4444' },
      { id: 'review',   label: '振り返り・復習',      emoji: '🔄', color: '#6366f1' },
      { id: 'other',    label: 'その他',              emoji: '📝', color: '#6b7280' },
    ];

    const toDate = (v) => {
      if (!v) return null;
      try {
        if (v?.toDate) return v.toDate();
        if (v?.seconds) return new Date(v.seconds * 1000);
        const d = new Date(v); return isNaN(d.getTime()) ? null : d;
      } catch { return null; }
    };

    const isThisMonth = (v) => {
      const d = toDate(v); if (!d) return false;
      const now = new Date();
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    };

    const fetchAllLogs = async () => {
      setLogsLoading(true);
      try {
        const snap = await getDocs(collectionGroup(db, 'logs'));
        const list = snap.docs.map(d => {
          const uid = d.ref.parent.parent?.id || '';
          return { id: d.id, uid, ...d.data() };
        });
        setAllLogs(list); setFetched(true);
      } catch(e) { console.error(e); }
      setLogsLoading(false);
    };

    useEffect(() => { fetchAllLogs(); }, []);

    const totalHours     = allLogs.reduce((s, l) => s + Number(l.hours || 0), 0);
    const thisMonthLogs  = allLogs.filter(l => isThisMonth(l.date || l.createdAt));
    const thisMonthHours = thisMonthLogs.reduce((s, l) => s + Number(l.hours || 0), 0);
    const activeUsers    = new Set(allLogs.map(l => l.uid)).size;
    const avgQuality     = (() => {
      const r = allLogs.filter(l => l.quality_rating > 0);
      return r.length > 0 ? (r.reduce((s,l) => s + l.quality_rating, 0) / r.length).toFixed(1) : '0.0';
    })();

    const catStats = CATEGORIES.map(c => {
      const cl = allLogs.filter(l => l.category === c.id);
      return { ...c, hours: Math.round(cl.reduce((s,l) => s + Number(l.hours||0), 0)*10)/10, count: cl.length };
    }).sort((a,b) => b.hours - a.hours);
    const maxCatHours = Math.max(...catStats.map(c => c.hours), 1);

    const qualityDist = [1,2,3,4,5].map(q => ({
      q, label: ['','😞','😐','🙂','😊','🌟'][q],
      count: allLogs.filter(l => l.quality_rating === q).length,
    }));
    const maxQCount = Math.max(...qualityDist.map(d => d.count), 1);

    const now = new Date();
    const monthlyHours = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5-i), 1);
      const hours = allLogs.filter(l => {
        const ld = toDate(l.date || l.createdAt);
        return ld && ld.getFullYear() === d.getFullYear() && ld.getMonth() === d.getMonth();
      }).reduce((s,l) => s + Number(l.hours||0), 0);
      return { label: `${d.getMonth()+1}月`, hours: Math.round(hours*10)/10 };
    });
    const maxMHours = Math.max(...monthlyHours.map(m => m.hours), 1);

    const userRanking = users.map(u => {
      const uLogs = allLogs.filter(l => l.uid === u.id);
      return { ...u, logCount: uLogs.length, totalHours: Math.round(uLogs.reduce((s,l) => s + Number(l.hours||0), 0)*10)/10 };
    }).sort((a,b) => b.totalHours - a.totalHours).slice(0, 5);

    if (logsLoading) return (
      <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>📊</div>
        <div>学習ログを集計中...</div>
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ ...styles.pageTitle, marginBottom: 0 }}>学習ログ分析</h2>
          <button onClick={fetchAllLogs} style={styles.btnSecondary}>🔄 再取得</button>
        </div>

        <div style={styles.statGrid}>
          {[
            { label: '総学習時間',      value: `${Math.round(totalHours*10)/10}h`, icon: '⏱️', color: '#3b82f6', sub: `全${allLogs.length}件` },
            { label: '今月の学習時間',  value: `${Math.round(thisMonthHours*10)/10}h`, icon: '📅', color: '#10b981', sub: `${thisMonthLogs.length}件` },
            { label: 'アクティブユーザー', value: `${activeUsers}人`, icon: '🔥', color: '#ef4444', sub: 'ログ記録あり' },
            { label: '平均品質スコア',  value: `${avgQuality}/5`, icon: '⭐', color: '#f59e0b', sub: '満足度' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📚 カテゴリ別学習時間</h3>
            {catStats.map(c => (
              <div key={c.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span>{c.emoji} {c.label}</span>
                  <span style={{ fontWeight: 700, color: c.color }}>{c.hours}h</span>
                </div>
                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 999 }}>
                  <div style={{ height: 6, width: `${(c.hours/maxCatHours)*100}%`, background: c.color, borderRadius: 999 }} />
                </div>
              </div>
            ))}
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>⭐ 品質評価の分布</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, padding: '4px 0' }}>
              {qualityDist.map(d => {
                const h = Math.round((d.count/maxQCount)*100);
                return (
                  <div key={d.q} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>{d.count}</span>
                    <div style={{ width: '100%', height: `${Math.max(h,4)}%`, minHeight: 4,
                      background: `hsl(${d.q*30+20},80%,60%)`, borderRadius: '4px 4px 0 0' }} />
                    <span style={{ fontSize: 14 }}>{d.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📅 月別総学習時間（過去6ヶ月）</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 140, padding: '8px 0' }}>
            {monthlyHours.map((m, i) => {
              const h = Math.round((m.hours/maxMHours)*100);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>{m.hours}h</span>
                  <div style={{ width: '100%', height: `${Math.max(h,4)}%`, minHeight: 4,
                    background: i === monthlyHours.length-1 ? 'linear-gradient(180deg,#10b981,#059669)' : 'linear-gradient(180deg,#93c5fd,#3b82f6)',
                    borderRadius: '6px 6px 0 0' }} />
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🏆 学習時間ランキング（上位5名）</h3>
          {userRanking.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%',
                background: ['#f59e0b','#9ca3af','#b45309','#e5e7eb','#e5e7eb'][i],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800, color: i < 3 ? '#fff' : '#6b7280', flexShrink: 0 }}>
                {i+1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{u.displayName || '未設定'}</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>{u.email}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#3b82f6' }}>{u.totalHours}h</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>{u.logCount}件</div>
              </div>
            </div>
          ))}
          {userRanking.every(u => u.totalHours === 0) && (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>学習ログがまだありません</div>
          )}
        </div>
      </div>
    );
  };


  // ── ベータ管理タブ ──
  const BetaTab = () => {
    const [betaUsers, setBetaUsers]       = useState([]);
    const [loading, setLoading]           = useState(true);
    const [searchQuery, setSearchQuery]   = useState('');
    const [inviteCode]                    = useState('NOBISHIRO2026');
    const [copied, setCopied]             = useState(false);
    const [updatingId, setUpdatingId]     = useState(null);
    const [feedbacks, setFeedbacks]       = useState([]);
    const [fbLoading, setFbLoading]       = useState(false);

    const toDate = (v) => {
      if (!v) return null;
      try {
        if (v?.toDate) return v.toDate();
        if (v?.seconds) return new Date(v.seconds * 1000);
        return new Date(v);
      } catch { return null; }
    };
    const fmt = (v) => { const d = toDate(v); return d ? d.toLocaleDateString('ja-JP') : '-'; };

    // ベータユーザー取得（全ユーザーから）
    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          const snap = await getDocs(collection(db, 'users'));
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .filter(u => u.role !== 'master')
            .sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
          setBetaUsers(list);
        } catch(e) { console.error(e); }
        setLoading(false);
      })();
    }, []);

    // フィードバック取得
    useEffect(() => {
      (async () => {
        setFbLoading(true);
        try {
          const snap = await getDocs(collection(db, 'beta_feedback'));
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
          setFeedbacks(list);
        } catch(e) { console.error(e); }
        setFbLoading(false);
      })();
    }, []);

    const handleCopyInvite = () => {
      const url = `https://www.nobishiro-competency.com/login?beta=${inviteCode}`;
      navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    };

    const handleRoleChange = async (userId, newRole) => {
      setUpdatingId(userId);
      try {
        await updateDoc(doc(db, 'users', userId), { role: newRole });
        setBetaUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      } catch(e) { alert(e.message); }
      setUpdatingId(null);
    };

    const handlePlanChange = async (userId, newPlan) => {
      setUpdatingId(userId);
      try {
        await updateDoc(doc(db, 'users', userId), {
          'subscription.planId': newPlan,
          'subscription.status': 'active',
        });
        setBetaUsers(prev => prev.map(u => u.id === userId
          ? { ...u, subscription: { ...u.subscription, planId: newPlan } } : u));
      } catch(e) { alert(e.message); }
      setUpdatingId(null);
    };

    const handleOnboardingReset = async (userId) => {
      if (!window.confirm('オンボーディングをリセットしますか？')) return;
      setUpdatingId(userId);
      try {
        await updateDoc(doc(db, 'users', userId), { onboardingCompleted: false });
        setBetaUsers(prev => prev.map(u => u.id === userId ? { ...u, onboardingCompleted: false } : u));
      } catch(e) { alert(e.message); }
      setUpdatingId(null);
    };

    const filtered = betaUsers.filter(u =>
      !searchQuery ||
      (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.displayName || '').includes(searchQuery) ||
      (u.nickname || '').includes(searchQuery)
    );

    const PLAN_COLORS = { free: '#9ca3af', student: '#3b82f6', standard: '#8b5cf6', business: '#f59e0b' };
    const PLAN_LABELS = { free: 'Free', student: 'Student', standard: 'Standard', business: 'Business' };
    const ROLE_COLORS = { admin: '#ef4444', user: '#6b7280' };

    const stats = {
      total:    betaUsers.length,
      free:     betaUsers.filter(u => (u.subscription?.planId || 'free') === 'free').length,
      paid:     betaUsers.filter(u => u.subscription?.planId && u.subscription.planId !== 'free').length,
      today:    betaUsers.filter(u => { const d = toDate(u.createdAt); return d && d.toDateString() === new Date().toDateString(); }).length,
    };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ ...styles.pageTitle, marginBottom: 0 }}>🧪 ベータ管理</h2>
        </div>

        {/* KPI */}
        <div style={styles.statGrid}>
          {[
            { label: '総ユーザー数', value: stats.total, icon: '👥', color: '#6366f1' },
            { label: '今日の新規',   value: stats.today, icon: '🆕', color: '#10b981' },
            { label: '有料ユーザー', value: stats.paid,  icon: '💎', color: '#f59e0b' },
            { label: 'Freeユーザー', value: stats.free,  icon: '🆓', color: '#9ca3af' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* 招待URL */}
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <h3 style={styles.cardTitle}>🔗 ベータ招待URL</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, background: '#f8fafc', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#374151', fontFamily: 'monospace', minWidth: 200 }}>
              {`https://www.nobishiro-competency.com/login?beta=${inviteCode}`}
            </div>
            <button onClick={handleCopyInvite}
              style={{ padding: '10px 20px', background: copied ? '#10b981' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {copied ? '✅ コピーしました' : '📋 URLをコピー'}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
            招待コード：<strong style={{ color: '#6366f1' }}>{inviteCode}</strong>　このURLをベータテスターに共有してください
          </div>
        </div>

        {/* ユーザー一覧 */}
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ ...styles.cardTitle, margin: 0 }}>👥 ベータユーザー一覧</h3>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="名前・メールで検索..."
              style={{ ...styles.input, width: 220, padding: '6px 12px', fontSize: 12 }}
            />
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>⏳ 読み込み中...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['ユーザー','登録日','プラン','ロール','オンボーディング','操作'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#4338ca', flexShrink: 0 }}>
                            {(u.nickname || u.displayName || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.nickname || u.displayName || '未設定'}</div>
                            <div style={{ fontSize: 10, color: '#9ca3af' }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(u.createdAt)}</span></td>
                      <td style={styles.td}>
                        <select value={u.subscription?.planId || 'free'} onChange={e => handlePlanChange(u.id, e.target.value)}
                          disabled={updatingId === u.id}
                          style={{ fontSize: 11, border: `1.5px solid ${PLAN_COLORS[u.subscription?.planId || 'free']}`, borderRadius: 8, padding: '3px 6px', color: PLAN_COLORS[u.subscription?.planId || 'free'], background: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                          {Object.entries(PLAN_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <select value={u.role || 'user'} onChange={e => handleRoleChange(u.id, e.target.value)}
                          disabled={updatingId === u.id}
                          style={{ fontSize: 11, border: `1.5px solid ${ROLE_COLORS[u.role] || '#e5e7eb'}`, borderRadius: 8, padding: '3px 6px', color: ROLE_COLORS[u.role] || '#374151', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>
                          <option value="user">一般</option>
                          <option value="admin">管理者</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 700,
                          background: u.onboardingCompleted ? '#dcfce7' : '#fef3c7',
                          color: u.onboardingCompleted ? '#166534' : '#92400e' }}>
                          {u.onboardingCompleted ? '✅ 完了' : '⏳ 未完了'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button onClick={() => handleOnboardingReset(u.id)} disabled={updatingId === u.id}
                          style={{ fontSize: 10, padding: '3px 8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          🔄 OBリセット
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
                  {searchQuery ? '検索結果がありません' : 'ユーザーがいません'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* フィードバック一覧 */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>💬 ベータフィードバック</h3>
          {fbLoading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>⏳ 読み込み中...</div>
          ) : feedbacks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
              フィードバックはまだありません
            </div>
          ) : feedbacks.map(fb => (
            <div key={fb.id} style={{ padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                    background: fb.type === 'bug' ? '#fef2f2' : fb.type === 'request' ? '#eff6ff' : '#f0fdf4',
                    color: fb.type === 'bug' ? '#dc2626' : fb.type === 'request' ? '#2563eb' : '#16a34a' }}>
                    {fb.type === 'bug' ? '🐛 バグ' : fb.type === 'request' ? '💡 要望' : '💬 感想'}
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{fb.userEmail || '匿名'}</span>
                </div>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmt(fb.createdAt)}</span>
              </div>
              <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.6 }}>{fb.message}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── 未実装タブ ──
  const ComingSoon = ({ label }) => (
    <div>
      <h2 style={styles.pageTitle}>{label}</h2>
      <div style={{ ...styles.card, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🚧</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>準備中</div>
        <div style={{ fontSize: 13, color: '#9ca3af' }}>このセクションは近日公開予定です</div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) return (
      <div style={{ textAlign: 'center', padding: 80, color: '#9ca3af' }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>⏳</div>
        <div>読み込み中...</div>
      </div>
    );
    switch(activeMenu) {
      case 'overview': return <OverviewTab />;
      case 'users':    return <UsersTab />;
      case 'notice':   return <NoticeTab />;
      case 'coaching': return <CoachingTab />;
      case 'revenue':  return <RevenueTab />;
      case 'logs':     return <LogsTab />;
      case 'settings': return <ComingSoon label="サービス設定" />;
      case 'teams':    return <TeamsTab />;
      case 'beta':     return <BetaTab />;
      default:         return <OverviewTab />;
    }
  };

  if (!isAdmin) return null;

  return (
    <div style={styles.wrap}>
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em' }}>
            NOBISHIRO
          </div>
          <div style={{ fontSize: 10, color: '#818cf8', marginTop: 2 }}>管理者パネル</div>
        </div>
        <nav style={{ padding: '12px 0' }}>
          {MENU.map(m => (
            <div key={m.id} onClick={() => setActiveMenu(m.id)}
              style={{ ...styles.menuItem, ...(activeMenu === m.id ? styles.menuItemActive : {}) }}>
              <span style={{ fontSize: 16 }}>{m.icon}</span>
              <span>{m.label}</span>
            </div>
          ))}
        </nav>
        <div style={styles.sidebarFooter}>
          <div style={{ fontSize: 11, color: '#6366f1', marginBottom: 4 }}>
            {user?.email}
          </div>
          <div style={{ fontSize: 10, color: '#4338ca', background: '#312e81',
            padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>
            ADMIN
          </div>
          <div style={{ marginTop: 12 }}>
            <button onClick={() => navigate('/dashboard')}
              style={{ fontSize: 11, color: '#818cf8', background: 'none',
                border: '1px solid #3730a3', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
              ← ダッシュボードへ
            </button>
          </div>
        </div>
      </aside>
      <main style={styles.main}>
        {renderContent()}
      </main>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  sidebar: { width: 220, background: '#1e1b4b', display: 'flex', flexDirection: 'column',
    flexShrink: 0, position: 'sticky', top: 0, height: '100vh' },
  sidebarHeader: { padding: '24px 20px 16px', borderBottom: '1px solid #312e81' },
  menuItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
    cursor: 'pointer', fontSize: 13, color: '#a5b4fc', transition: 'all 0.15s' },
  menuItemActive: { background: '#312e81', color: '#fff', borderRight: '3px solid #818cf8' },
  sidebarFooter: { marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid #312e81' },
  main: { flex: 1, padding: '32px 36px', overflowY: 'auto' },
  pageTitle: { fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 24 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 16, marginBottom: 24 },
  statCard: { background: '#fff', borderRadius: 12, padding: '20px 16px',
    border: '1px solid #e5e7eb', textAlign: 'center' },
  card: { background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #e5e7eb', marginBottom: 20 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
    color: '#6b7280', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 12px', verticalAlign: 'middle' },
  input: { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 12px',
    fontSize: 13, outline: 'none', color: '#111827', background: '#fff' },
  select: { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 12px',
    fontSize: 13, outline: 'none', color: '#111827', background: '#fff', cursor: 'pointer' },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 },
  btnPrimary: { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff',
    border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 13,
    fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb',
    borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
